/**
 * Pure token-label placement solver. Board token labels collide when the local cluster is dense —
 * two size-1 tokens in adjacent columns put their under-token labels ~40px apart while each label
 * is ~66px wide, guaranteeing overlap even when the name itself is short (`Cover (1/2)`).
 *
 * Three placement families, tried in that order:
 *   - **Inside** — a unit whose footprint is {@link INSIDE_MIN_CELLS}+ cells across hosts its own
 *     label *within* its own token rect (see {@link insideOffsetFor}). A size-3 or size-4 unit's
 *     rect is wide enough to swallow a label outright, and parking it there stops the big unit from
 *     eating its small neighbours' outside slots.
 *   - **Outside** — a candidate list ({@link candidatesFor}) that opens with the 8 fixed compass
 *     slots ({@link SLOT_OFFSETS}) and continues with positions slid along each side of the token
 *     rect at {@link SLIDE_STEP}. Every collision-free candidate is scored by
 *     {@link candidateCost} and the cheapest wins; a greedy pass in id order is followed by a
 *     bounded improvement pass ({@link MAX_IMPROVEMENT_MOVES}).
 *   - **Hidden** — when NO candidate is collision-free the label is not drawn at all, rather than
 *     drawn on top of a token or a neighbour's name. The renderer ticks the token at the label
 *     anchor and reveals the pill on hover/selection. Every placement this module returns is
 *     therefore collision-free; see {@link PlacedWhere}.
 *
 * Deliberately **rects in, offsets out** — no `Combatant`, no Pixi, no world/camera knowledge.
 * The renderer is the only caller that knows about tokens; this module is the reuse seam for any
 * future board-annotation placement, so don't grow a second solver beside it. That is also why the
 * inside threshold is a caller-supplied cell COUNT ({@link LabelItem.footprintCells}) rather than
 * something derived from the rect: deriving it would require the solver to know the caller's cell
 * size, which is exactly the world knowledge this module refuses to hold.
 */

/** An axis-aligned pixel rectangle in whatever coordinate space the caller passes in (the renderer
 *  passes WORLD-space token/label rects, not screen-space). `{x,y}` is the top-left corner. */
export type Rect = { x: number; y: number; w: number; h: number };

export type LabelItem = {
  id: string;
  token: Rect;
  label: { w: number; h: number };
  /** The **candidate index** this label occupied on the PREVIOUS solve, if any — i.e. the `slot`
   *  the previous {@link solveLabelLayout} reported for this same `id`, fed straight back in.
   *  Every other candidate pays {@link CHANGE_PENALTY}, which is what kills solve-churn when an
   *  unrelated token moves elsewhere on the board.
   *
   *  It is an INDEX, not a position, and that is the whole contract: {@link candidatesFor} builds
   *  the same list in the same order for a given `(token, label)` size pair, so index `k` means the
   *  same place on the next solve. It does NOT survive a change in the token's or the label's
   *  dimensions — index `k` is then a different offset — but a label whose own size changed is
   *  exactly a label that should be free to move, so nothing is owed there. An out-of-range value
   *  (a stale index from an older candidate list) is ignored, not clamped. */
  prevSlot?: number;
  /** The unit's footprint side in **cells** — `combatCells.ts`'s `footprintBox().n`, i.e. 1 for a
   *  size-1 unit, 3 for a size-3, 4 for a size-4. Absent means 1. Its ONLY use is the
   *  {@link INSIDE_MIN_CELLS} threshold for inside placement; the solver reads no other meaning
   *  from it and still does all of its geometry off {@link LabelItem.token}. It is a separate field
   *  rather than `token.w / cellSize` because this module deliberately doesn't know the caller's
   *  cell size (see the module doc). */
  footprintCells?: number;
  /** The lowest an inside label's bottom edge may sit, in token-local px from the token rect's top.
   *  The renderer derives it from what it draws along the token's lower edges (the hex token's HP
   *  trim, `hexGeometry.ts`'s `insideLabelFloor`); absent means {@link INSIDE_BOTTOM_INSET} above the
   *  rect's bottom edge. */
  insideBottom?: number;
};

/**
 * What KIND of placement a label got — the renderer's read, and the only thing it needs in order to
 * decide how to draw the callout:
 *
 *   - `"inside"` — within the unit's own token rect ({@link insideOffsetFor}), what a size-2+ unit
 *     gets. No leader line: the pill is already sitting on the thing it names.
 *   - `"attached"` — candidate 0, the plain under-token position, and the ONLY candidate on the
 *     small {@link GAP}. No leader line either: at 2px there is nothing for a line to span, and a
 *     label directly under its token needs no attribution.
 *   - `"displaced"` — any other outside candidate (index `>= 1`), all of which sit on the larger
 *     {@link DISPLACED_GAP}. **This is exactly the set that draws a leader line.**
 *   - `"hidden"` — no candidate was collision-free, so the label is NOT DRAWN rather than drawn on
 *     top of something (Phase 3.1 of `projects/crowded-label-placement.md`). The renderer marks the
 *     token with a small tick at the label anchor so the player knows a name exists, and reveals the
 *     pill on hover/selection.
 *
 * `where` is an ADDITION beside {@link PlacedLabel.slot}, not a replacement for it: `slot` is the
 * candidate index {@link LabelItem.prevSlot} feeds back, and an enum cannot carry an index.
 */
export type PlacedWhere = "inside" | "attached" | "displaced" | "hidden";

export type PlacedLabel = {
  /** The offset from the token rect's top-left to the label rect's top-left.
   *
   *  For `"inside"`/`"attached"`/`"displaced"` this is where the pill is DRAWN. For `"hidden"` it is
   *  the label **anchor** — candidate 0's offset, i.e. just under the token's bottom centre — which
   *  is where the renderer puts the tick and where the hover/selection reveal draws the pill. It is
   *  deliberately still a real offset rather than absent: a hidden entry still has to follow its
   *  token across a drag, and a caller that had to invent the anchor itself would be re-deriving
   *  candidate 0's formula outside this module. */
  dx: number;
  dy: number;
  /** Which KIND of placement this is — see {@link PlacedWhere}. The renderer's whole drawing rule.
   *  Derived from the solve in one place here so no caller re-states it from an index comparison it
   *  would have to keep in sync with the candidate list's shape. */
  where: PlacedWhere;
  /** Which outside candidate this label sits at — a real index into {@link candidatesFor}'s list,
   *  never a sentinel. Indices `0..7` are still the eight compass slots in their original order;
   *  `8` and up are the slid positions 2.1 added.
   *
   *  **Present iff `where` is `"attached"` or `"displaced"`.** An `"inside"` placement is not at any
   *  candidate, and a `"hidden"` one is not placed at all, so neither has a truthful index to report
   *  and the field is simply not there.
   *
   *  Its job is {@link LabelItem.prevSlot}: it is fed straight back into the next solve so a settled
   *  label stays put, which is why {@link PlacedWhere} could not replace it. `slot` answers "which
   *  candidate"; `where` answers "what kind of placement". Keeping both is what lets the renderer
   *  read the second without the solver losing the first. A hidden label correctly feeds back
   *  `undefined` — it has no placement to be loyal to, so when one opens up it is free to take the
   *  cheapest one rather than paying {@link CHANGE_PENALTY} to leave a position it never held. */
  slot?: number;
};

/** 2px clearance between a label and any obstacle (token or another label) — small enough to
 *  read as "attached", large enough that adjacent AABBs never touch-and-flap under float noise.
 *  This is the **attached** gap and it belongs to slot 0 alone; every displaced slot uses
 *  {@link DISPLACED_GAP}. */
const GAP = 2;

/**
 * The gap for a **displaced** label (any slot but 0). Deliberately larger than {@link GAP}, and the
 * reason is the leader line rather than collision: a displaced label is the one case that draws a
 * line back to its token (`pixiCombatBoard.ts` draws one iff `where` is `displaced`), and at a 2px offset
 * that line has essentially nothing to span — it satisfies the callout design's letter while
 * defeating its purpose, which is *attribution when displaced*. 8px is the conservative FLOOR of a
 * recommended 8-10px range — the smallest step that still buys a visible span without pushing the
 * label far enough to look detached from the entity it names. **It has NOT been confirmed to read
 * by a human eye**; it was chosen on geometry, and a unit test can only prove the gap is what we
 * intended, never that the line registers. The board's default view scale is 1, so this is 8 screen
 * pixels at default zoom (~11px on the four diagonal slots), drawn as a 1px stroke. If it still
 * doesn't register on a crowded board, this constant is the first dial to turn and the leader
 * stroke's alpha/width (`pixiCombatBoard.ts`) is the second.
 *
 * Only the *offset* grows. The collision test itself is unchanged (strict AABB overlap), so this
 * does not loosen what counts as a collision — it just parks a displaced label a little further out.
 * Slot 0 keeps {@link GAP} exactly, which is what preserves the pixel-identity property: a label
 * with no neighbour conflict renders where it always did.
 */
const DISPLACED_GAP = 8;

/** Which side of the token rect a candidate hangs off. The four diagonal compass slots are their
 *  own class (`corner`) rather than being folded into a side: they are the hardest positions to
 *  read back to their token, so the cost function prices them separately ({@link SIDE_PENALTY}). */
type CandidateSide = "S" | "N" | "E" | "W" | "corner";

/** One outside placement option: the `{dx,dy}` offset from the token rect's top-left to the label
 *  rect's top-left, plus the side it sits on. The label's placed bbox is
 *  `{x: token.x + dx, y: token.y + dy, w: label.w, h: label.h}`. */
type Candidate = { dx: number; dy: number; side: CandidateSide };

/**
 * The 8 compass slots, in their original fixed order: S (today's under-center position — slot 0, so
 * a sparse board renders exactly as today), N, SE, SW, NE, NW, E, W.
 *
 * They stay the FIRST eight entries of {@link candidatesFor}'s list, and that is what keeps the
 * uncontested case pixel-identical to the pre-2.1 solver: slot 0 is the cheapest candidate there is
 * (nearest its own token, no leader span to pay for, on the preferred side), and ties between
 * equal-cost candidates resolve to the lowest index, which is still the old preference order.
 *
 * Slot 0 uses {@link GAP}; slots 1-7 use the larger {@link DISPLACED_GAP} so their leader lines have
 * something to span.
 */
const SLOT_OFFSETS: { side: CandidateSide; at: (token: Rect, label: { w: number; h: number }) => { dx: number; dy: number } }[] = [
  // 0: S — under-center (today's static position, the ONLY slot on the attached `GAP`).
  { side: "S", at: (token, label) => ({ dx: (token.w - label.w) / 2, dy: token.h + GAP }) },
  // 1: N — above-center.
  { side: "N", at: (token, label) => ({ dx: (token.w - label.w) / 2, dy: -label.h - DISPLACED_GAP }) },
  // 2: SE — below-right, diagonal off the token's bottom-right corner.
  { side: "corner", at: (token, _label) => ({ dx: token.w + DISPLACED_GAP, dy: token.h + DISPLACED_GAP }) },
  // 3: SW — below-left, diagonal off the token's bottom-left corner.
  { side: "corner", at: (token, label) => ({ dx: -label.w - DISPLACED_GAP, dy: token.h + DISPLACED_GAP }) },
  // 4: NE — above-right, diagonal off the token's top-right corner.
  { side: "corner", at: (token, label) => ({ dx: token.w + DISPLACED_GAP, dy: -label.h - DISPLACED_GAP }) },
  // 5: NW — above-left, diagonal off the token's top-left corner.
  { side: "corner", at: (token, label) => ({ dx: -label.w - DISPLACED_GAP, dy: -label.h - DISPLACED_GAP }) },
  // 6: E — right side, vertically centered.
  { side: "E", at: (token, label) => ({ dx: token.w + DISPLACED_GAP, dy: (token.h - label.h) / 2 }) },
  // 7: W — left side, vertically centered.
  { side: "W", at: (token, label) => ({ dx: -label.w - DISPLACED_GAP, dy: (token.h - label.h) / 2 }) },
];

/**
 * How far apart the slid candidates sit along a side, **in pixels of whatever space the caller's
 * rects are in**.
 *
 * 20 is half of the board renderer's 40px cell, and that is why the number is 20: a label that
 * slides by less than half a cell rarely clears the neighbour that blocked it, and one that slides
 * by more skips over the gaps a size-1 unit leaves. But the solver never divides a rect by a cell
 * size to get here and is never told one (see the module doc and {@link LabelItem.footprintCells}) —
 * it is a flat pixel constant, and a caller working in some other pixel space gets a step of 20 of
 * its own pixels. The alternative considered was taking the step as a solver argument; rejected
 * because the one real caller would pass this same value forever, and a parameter nobody varies is
 * a parameter that goes stale.
 */
const SLIDE_STEP = 20;

/**
 * How many steps out from a side's centre the slid candidates go, in each direction. 4 steps ×
 * {@link SLIDE_STEP} = 80px = two 40px cells of travel either way, which is what it takes for a
 * size-1 unit pinned against a size-3's footprint to reach past its neighbours' labels. Raising it
 * costs solve time (the candidate list is `8 + 8 * this` long) and lets a label wander far enough
 * from its token that the leader line stops being convincing. The cost function's distance term
 * already discourages the far ones, so this is a hard stop rather than the main dial.
 */
const SLIDE_STEPS_PER_SIDE = 4;

/** The four sides that slide, in the order their candidates are appended — S first because it is
 *  the preferred side, then N, then the two flanks. */
const SLIDE_SIDES: CandidateSide[] = ["S", "N", "E", "W"];

/**
 * The full outside candidate list for one item, in a **deterministic order that depends only on the
 * token and label dimensions**: the eight compass slots ({@link SLOT_OFFSETS}) at indices 0-7, then,
 * for each side in {@link SLIDE_SIDES}, that side's centre position slid by
 * ±1…±{@link SLIDE_STEPS_PER_SIDE} steps of {@link SLIDE_STEP} — nearest first, negative before
 * positive. That determinism is what lets {@link LabelItem.prevSlot} be a bare index.
 *
 * The slid candidates all sit on {@link DISPLACED_GAP}: they are displaced by definition, so they
 * draw a leader line and it needs a span. A side's step-0 position is deliberately not re-emitted —
 * for S and N it is already slot 0 and slot 1, for E and W it is already slot 6 and slot 7.
 */
function candidatesFor(token: Rect, label: { w: number; h: number }): Candidate[] {
  const out: Candidate[] = SLOT_OFFSETS.map((slot) => ({ ...slot.at(token, label), side: slot.side }));
  const centreDx = (token.w - label.w) / 2;
  const centreDy = (token.h - label.h) / 2;
  for (const side of SLIDE_SIDES) {
    for (let step = 1; step <= SLIDE_STEPS_PER_SIDE; step++) {
      for (const sign of [-1, 1]) {
        const slide = sign * step * SLIDE_STEP;
        if (side === "S") out.push({ dx: centreDx + slide, dy: token.h + DISPLACED_GAP, side });
        else if (side === "N") out.push({ dx: centreDx + slide, dy: -label.h - DISPLACED_GAP, side });
        else if (side === "E") out.push({ dx: token.w + DISPLACED_GAP, dy: centreDy + slide, side });
        else out.push({ dx: -label.w - DISPLACED_GAP, dy: centreDy + slide, side });
      }
    }
  }
  return out;
}

/**
 * The smallest footprint, in cells, that hosts its own label INSIDE its token rect. A size-1 unit's
 * 1-cell rect is narrower than any real label, so 2 is the first width where the question can even
 * be asked; the *fit* check in {@link insideOffsetFor} is what decides it for real.
 */
const INSIDE_MIN_CELLS = 2;

/**
 * Gap between an inside label's bottom edge and the bottom edge of its own footprint. The pill sits
 * in the footprint's **lower band** — horizontally centred, pushed down off the token's centred
 * glyph/silhouette rather than over it. 4px is a hairline that keeps the pill visibly *within* the
 * footprint outline instead of looking flush with its border; like {@link DISPLACED_GAP} it is
 * chosen on geometry and has NOT been confirmed by a human eye (Phase 3.3 of
 * `projects/crowded-label-placement.md` is the eyeball round that settles it).
 */
const INSIDE_BOTTOM_INSET = 4;

/**
 * The offset for an inside placement, or `undefined` when this item doesn't get one. Two conditions,
 * both required:
 *   - the footprint is {@link INSIDE_MIN_CELLS}+ cells across, and
 *   - the label actually FITS within the token rect at the inset — otherwise the placement would
 *     hang off its own footprint, which would break the one property this whole module exists to
 *     hold ("a label never occludes a token that is not its own") the moment a long name met a
 *     size-2 unit. A non-fitting big unit falls through to the ordinary outside slots.
 *
 * Because it fits by construction, the returned rect is always fully contained by `item.token` —
 * that containment is what `frontend/e2e/label-layout.e2e.ts` asserts on the live renderer.
 */
function insideOffsetFor(item: LabelItem): { dx: number; dy: number } | undefined {
  if ((item.footprintCells ?? 1) < INSIDE_MIN_CELLS) return undefined;
  const dx = (item.token.w - item.label.w) / 2;
  const dy = (item.insideBottom ?? item.token.h - INSIDE_BOTTOM_INSET) - item.label.h;
  if (dx < 0 || dy < 0) return undefined;
  return { dx, dy };
}

/** Strict AABB overlap — edges touching (the slot's gap already separates them) do not count as a
 *  collision. */
function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Edge-to-edge distance between two AABBs — 0 on an axis where they overlap, so the hypot is the
 *  straight-line gap a corner-to-corner connector would cross. That is exactly what the renderer's
 *  leader line spans (`pixiCombatBoard.ts` clamps both endpoints onto their own rect's boundary),
 *  which is why {@link candidateCost} prices it as the "leader length" term. */
function rectSeparation(a: Rect, b: Rect): number {
  const gapX = Math.max(0, a.x - (b.x + b.w), b.x - (a.x + a.w));
  const gapY = Math.max(0, a.y - (b.y + b.h), b.y - (a.y + a.h));
  return Math.hypot(gapX, gapY);
}

/** The label bbox an item would occupy at a given candidate offset. */
function rectAt(item: LabelItem, candidate: { dx: number; dy: number }): Rect {
  return { x: item.token.x + candidate.dx, y: item.token.y + candidate.dy, w: item.label.w, h: item.label.h };
}

/**
 * Weight on how far a candidate parks the label's centre from its own token's centre. This is the
 * term that keeps a label near the thing it names, and it is the term that makes slot 0 win an
 * uncontested board: every other candidate is strictly further out.
 *
 * The three geometric weights below are all in **pixels**, so the cost is a pixel number and the
 * side penalties read as "how many pixels of extra travel this preference is worth". Keep it that
 * way — a unitless weight here would make the constants unarguable.
 */
const DISTANCE_WEIGHT = 1;

/** Weight on the leader line's span ({@link rectSeparation} from the label back to its own token).
 *  It overlaps with {@link DISTANCE_WEIGHT} but is not the same fact: centre-to-centre distance
 *  grows with the label's own width, while the span is what the player's eye has to follow. Pricing
 *  both is what makes a long label hugging its token beat a short one flung a cell away. */
const LEADER_WEIGHT = 1;

/**
 * What each side costs relative to S, in pixels of equivalent travel. S is free (it is where a
 * label goes when nothing is in the way, and the whole board reads the same way as a result), N is
 * the cheap alternative because it is still centred over the token, and the flanks cost more
 * because a label beside a token is easy to misread as belonging to its neighbour. The four
 * diagonal compass slots are worst: they are the hardest to attribute at a glance.
 *
 * These are deliberately SMALL next to the geometric terms — they break ties between candidates at
 * comparable distance, they do not send a label two cells away to stay on the south side.
 */
const SIDE_PENALTY: Record<CandidateSide, number> = { S: 0, N: 4, E: 8, W: 8, corner: 12 };

/**
 * What it costs to move a label off the candidate it held on the previous solve
 * ({@link LabelItem.prevSlot}).
 *
 * **Solve churn is this solver's known failure mode** (`projects/completed/token-label-layout.md`,
 * Gotchas): the solve is globally coupled, so without this term dragging one token across the board
 * makes every label in an unrelated cluster re-shuffle. 1000 is chosen to be strictly larger than
 * the span of the geometric terms over any plausible candidate set — with a 160px token, an 88px
 * label and the full ±80px of slide, the distance and leader terms together top out around 570 —
 * so a settled label NEVER moves for a cheaper position, only because its own candidate stopped
 * being collision-free. That is the intended behaviour, not an approximation of it: the cost
 * function decides *where* a label goes, and this constant decides *whether* it goes anywhere.
 */
const CHANGE_PENALTY = 1000;

/** What a HIDDEN placement adds to an item's cost for the improvement pass's purposes. Larger than
 *  {@link CHANGE_PENALTY} on purpose: a name the player cannot read at all is worth more than any
 *  amount of churn, so a hidden item is always the first one the improvement pass tries to re-place
 *  and it will take any collision-free candidate at all — including one it has to evict a settled
 *  neighbour's label out of ({@link tryPairMove}). */
const DEGRADED_PENALTY = 10000;

/** Float slack for "strictly cheaper". Candidate costs are hypots, so exact `<` on two arithmetically
 *  equal costs can flap; a move must beat the incumbent by more than this to be taken, which is also
 *  what guarantees the improvement pass terminates. */
const COST_EPSILON = 1e-9;

/**
 * How many moves the improvement pass may make. The greedy pass is order-dependent — an item decided
 * early takes the position an item decided later needed more — and this is the bounded repair for
 * that. Each move strictly lowers the total of {@link effectiveCost} and leaves every uninvolved
 * item's cost untouched, so the pass cannot cycle; the cap is a hard stop on solve time, not a
 * correctness requirement, and stopping at it just leaves a solve less improved than it could be.
 *
 * **The only move that can actually help is the PAIR move, and that is worth knowing before
 * touching this.** The obvious repair — re-place the worst item on its own — is provably a no-op
 * after a cost-greedy pass: greedy gives each item the cheapest candidate that was clean when it was
 * decided, and later decisions only ADD blockers, so an item's chosen candidate is still the
 * cheapest of the (now smaller) clean set it can reach. That was measured before it was replaced —
 * **zero** single-item moves fired across all 60 dense property cases. What escapes greedy's
 * ordering is moving the item that is IN THE WAY: {@link tryPairMove}.
 *
 * 12 is where the returns stop, measured rather than guessed. On the 60-case dense-cluster corpus in
 * `labelLayout.test.ts` (30 tokens crammed into 25 cells — far denser than any real board) the pass
 * makes 586 moves in total and 27 of the 60 cases do run into the cap, but raising it changes almost
 * nothing: at 32, 64 and 128 the corpus's degraded-placement count moves from 211 to 209 out of 1800
 * and stops, for ~30% more solve time. The cap is not what limits those cases. For scale, the
 * pre-2.2 first-fit solver degraded ~42% of the same corpus; this is 11.7%.
 */
const MAX_IMPROVEMENT_MOVES = 12;

/**
 * What one candidate costs this item. Lower wins. Collision is NOT priced here — it is a hard
 * filter applied by {@link bestCandidateFor}, so a colliding candidate is never selectable while
 * any collision-free one exists, and no weight can trade a legible board away for a tidier one.
 */
function candidateCost(item: LabelItem, candidate: Candidate, index: number): number {
  const rect = rectAt(item, candidate);
  const centreDistance = Math.hypot(
    rect.x + rect.w / 2 - (item.token.x + item.token.w / 2),
    rect.y + rect.h / 2 - (item.token.y + item.token.h / 2),
  );
  const changed = item.prevSlot !== undefined && item.prevSlot !== index ? CHANGE_PENALTY : 0;
  return DISTANCE_WEIGHT * centreDistance + LEADER_WEIGHT * rectSeparation(rect, item.token) + SIDE_PENALTY[candidate.side] + changed;
}

/**
 * One item's outside placement, as the solve carries it between passes.
 *
 * `hidden` is the degraded state: the item found no collision-free candidate, so `index`/`dx`/`dy`/
 * `rect` describe candidate 0 — the label ANCHOR, what the renderer draws its tick at and where a
 * hover reveal puts the pill — and **`rect` is deliberately NOT in the solve's blocker array.** A
 * hidden label is not drawn, so it occupies nothing and must not push a neighbour off a position
 * that is in fact clear. That absence is what {@link adoptPlacement} has to special-case when the
 * improvement pass un-hides an item.
 */
type Placement = { item: LabelItem; index: number; dx: number; dy: number; rect: Rect; cost: number; hidden: boolean };

/** A hidden placement's rank for the improvement pass — see {@link DEGRADED_PENALTY}. */
function effectiveCost(placement: Placement): number {
  return placement.cost + (placement.hidden ? DEGRADED_PENALTY : 0);
}

/**
 * The cheapest collision-free candidate for `item`, or `undefined` when every candidate collides.
 * `blockers` is everything the label must avoid (token bboxes, caller obstacles, other labels'
 * rects); `exclude` is the one blocker that IS this item's current label rect, skipped by reference
 * so the improvement pass doesn't find the item colliding with itself.
 *
 * Ties go to the lowest candidate index, which is what preserves the pre-2.1 preference order
 * (S, N, SE, SW, NE, NW, E, W, then the slid positions) whenever two candidates cost the same.
 */
function bestCandidateFor(item: LabelItem, blockers: Rect[], exclude: Rect | undefined): Placement | undefined {
  let best: Placement | undefined;
  for (const [index, candidate] of candidatesFor(item.token, item.label).entries()) {
    const rect = rectAt(item, candidate);
    if (blockers.some((b) => b !== exclude && rectsOverlap(rect, b))) continue;
    const cost = candidateCost(item, candidate, index);
    if (best && cost >= best.cost) continue;
    best = { item, index, dx: candidate.dx, dy: candidate.dy, rect, cost, hidden: false };
  }
  return best;
}

/**
 * The degradation floor: the label is HIDDEN. Every candidate collided, and Phase 3.1 chose not
 * drawing the label over drawing it on top of a token or a neighbour's name.
 *
 * `index`/`dx`/`dy`/`rect` describe candidate 0 — the label anchor. That is what the renderer needs
 * (a tick under the token's bottom centre, and somewhere to put the pill when hover reveals it) and
 * it is what gives the improvement pass a `cost` to compare against; it is NOT a placement, and
 * {@link solveLabelLayout} never puts this rect in the blocker array nor reports a `slot` for it.
 */
function hiddenFor(item: LabelItem): Placement {
  const candidate = candidatesFor(item.token, item.label)[0]!;
  return { item, index: 0, dx: candidate.dx, dy: candidate.dy, rect: rectAt(item, candidate), cost: candidateCost(item, candidate, 0), hidden: true };
}

/** Overwrite a placement in place, keeping `blockers` pointing at the new rect. `Placement.rect` is
 *  the identity the other passes exclude "itself" by, so the two have to be swapped together.
 *
 *  A HIDDEN placement's rect is not in `blockers` at all (see {@link Placement}), so un-hiding an
 *  item APPENDS its freshly-placed rect instead of swapping one. The reverse never happens — the
 *  improvement pass only ever moves an item onto a real candidate, so no placement here goes from
 *  drawn back to hidden and nothing is ever removed from `blockers`. */
function adoptPlacement(placement: Placement, next: Placement, blockers: Rect[]): void {
  const at = blockers.indexOf(placement.rect);
  if (at === -1) blockers.push(next.rect);
  else blockers[at] = next.rect;
  placement.index = next.index;
  placement.dx = next.dx;
  placement.dy = next.dy;
  placement.rect = next.rect;
  placement.cost = next.cost;
  placement.hidden = next.hidden;
}

/**
 * The improvement pass's one move: `target` takes a candidate that exactly ONE other label is
 * sitting on, and that occupant is re-placed out of the way. Applied, and `true`, when the pair's
 * combined {@link effectiveCost} strictly drops; otherwise nothing changes and `false`.
 *
 * Why a pair and not a solo re-place: see {@link MAX_IMPROVEMENT_MOVES}. A solo move provably cannot
 * help after a cost-greedy pass, because the position `target` wants is occupied — the only way to
 * reach it is to move the occupant. Restricting the move to candidates blocked by exactly one LABEL
 * is what keeps it bounded and the accounting exact: a candidate blocked by a token or an obstacle
 * is not negotiable at all, and one blocked by two labels would need a three-way move this
 * deliberately doesn't attempt.
 *
 * Both new rects are collision-free against the final set by construction — the occupant is
 * re-placed against a blocker set that already holds `target`'s new rect and no longer holds its
 * own, and `target`'s new rect was required to hit nothing but the occupant. No third item's rect
 * moves, so no third item's cost or cleanliness changes, which is what makes the pass monotone.
 *
 * It is a strict improvement in the pair's TOTAL, which means the occupant may legitimately end up
 * on a worse candidate than it had. That is the point: it is how a HIDDEN item gets drawn at all
 * (via {@link DEGRADED_PENALTY}), and it is bounded by the same {@link CHANGE_PENALTY} every other
 * move pays, so a settled occupant is not cheap to evict.
 *
 * A hidden `target` is handled by the same code with no special case: its own rect isn't in
 * `blockers`, so the `b !== target.rect` filter is simply a no-op for it, and {@link adoptPlacement}
 * appends rather than swaps when it lands somewhere.
 */
function tryPairMove(target: Placement, outside: Placement[], blockers: Rect[]): boolean {
  for (const [index, candidate] of candidatesFor(target.item.token, target.item.label).entries()) {
    const rect = rectAt(target.item, candidate);
    const hit = blockers.filter((b) => b !== target.rect && rectsOverlap(rect, b));
    if (hit.length !== 1) continue;
    const occupant = outside.find((p) => p.rect === hit[0]);
    if (!occupant || occupant === target) continue;

    const trialBlockers = [...blockers.filter((b) => b !== target.rect && b !== occupant.rect), rect];
    const rehoused = bestCandidateFor(occupant.item, trialBlockers, undefined);
    if (!rehoused) continue;

    const cost = candidateCost(target.item, candidate, index);
    if (cost + rehoused.cost + COST_EPSILON >= effectiveCost(target) + effectiveCost(occupant)) continue;

    adoptPlacement(occupant, rehoused, blockers);
    adoptPlacement(target, { item: target.item, index, dx: candidate.dx, dy: candidate.dy, rect, cost, hidden: false }, blockers);
    return true;
  }
  return false;
}

/**
 * Solve. Deterministic end to end: items are processed in `id` order (not array order, so shuffled
 * input yields identical output), the candidate list is a pure function of the token and label
 * dimensions, and every tie resolves to the lowest candidate index.
 *
 * Two passes:
 *
 *   1. **Greedy**, in id order. An item that qualifies for an **inside** placement
 *      ({@link insideOffsetFor}) takes it unconditionally — no collision test, because the label
 *      sits within its own token rect and so can only ever overlap the one token it names.
 *      Otherwise the item takes the cheapest ({@link candidateCost}) collision-free candidate from
 *      {@link candidatesFor}. If every candidate collides the label is **hidden**
 *      ({@link hiddenFor}) rather than drawn on top of something.
 *   2. **Bounded improvement** ({@link MAX_IMPROVEMENT_MOVES}). Greedy is order-dependent, so this
 *      repeatedly takes the worst-off item and tries the one move that can help it — {@link
 *      tryPairMove}, which puts it on a candidate another label is occupying and re-places that
 *      occupant. Every move strictly lowers the pair's combined cost and changes nobody else's, so
 *      the solve's total cost falls monotonically and the pass terminates on its own, well before
 *      the cap on all but the densest boards.
 *
 * **Every placement this returns is collision-free, and there is no third pass any more.** Phase 3.1
 * deleted a re-validation sweep that existed for exactly one case: the forward-only greedy pass
 * checks an item only against items decided before it, so a later item's forced FALLBACK — a
 * placement that accepted collision by design — could land on top of an earlier item's confirmed
 * clean one, and the sweep flipped that earlier item's `collides` flag after the fact. Hiding
 * removed the cause: a hidden label contributes no rect, so nothing a later item does can stomp an
 * earlier placement, and the downgrade can no longer occur. The invariant is asserted from outside
 * instead, over the whole 60-case dense corpus, in `labelLayout.test.ts`'s property test — the right
 * place for it, since re-checking it on every solve would only re-derive in production what the
 * greedy pass already guarantees.
 *
 * `obstacles` are rects drawn on the board that are NOT token rects and NOT labels — the size-1
 * HP/heat bars `projects/turn-state-on-the-map.md` draws outside a token rect are the motivating
 * case. They are in the same coordinate space as `items[].token`, they block outside candidates
 * exactly as a token does, and they are never placed into (the solver only ever reads them). Empty
 * by default, which is the current renderer's call and reproduces the pre-obstacle behaviour exactly.
 *
 * **An inside label is deliberately absent from the blocker set the outside candidates consult.** It
 * is geometrically contained by its own token rect, which every outside candidate already avoids, so
 * adding it would reject candidates that are in fact clear — it would shrink the neighbours' options
 * for nothing. That is the whole point of moving a big unit's label inside. (For the same reason
 * an inside placement is never collision-tested at all: it overlaps its own token on purpose, so any
 * such test would reject every one of them.)
 */
export function solveLabelLayout(items: LabelItem[], obstacles: Rect[] = []): Map<string, PlacedLabel> {
  const result = new Map<string, PlacedLabel>();
  /** Everything an outside candidate must avoid, in one array: token bboxes, caller obstacles, and
   *  each OUTSIDE label rect as it is decided. A `Placement.rect` is the very object sitting in
   *  here, which is how the later passes exclude "itself" by reference rather than by a parallel
   *  index that the inside skips would desynchronize. */
  const blockers: Rect[] = [...items.map((it) => it.token), ...obstacles];
  const outside: Placement[] = [];
  const ordered = [...items].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const item of ordered) {
    const insideOffset = insideOffsetFor(item);
    if (insideOffset) {
      result.set(item.id, { dx: insideOffset.dx, dy: insideOffset.dy, where: "inside" });
      continue;
    }
    const placement = bestCandidateFor(item, blockers, undefined) ?? hiddenFor(item);
    // A hidden label is not drawn, so it blocks nothing — see `Placement`'s doc.
    if (!placement.hidden) blockers.push(placement.rect);
    outside.push(placement);
  }

  for (let move = 0; move < MAX_IMPROVEMENT_MOVES; move++) {
    const worstFirst = [...outside].sort((a, b) => effectiveCost(b) - effectiveCost(a) || (a.item.id < b.item.id ? -1 : 1));
    if (!worstFirst.some((placement) => tryPairMove(placement, outside, blockers))) break;
  }

  for (const placement of outside) {
    if (placement.hidden) {
      // `dx`/`dy` is candidate 0 — the label ANCHOR, not a placement — and there is no `slot`,
      // because the label does not sit at any candidate. See `PlacedLabel`'s doc.
      result.set(placement.item.id, { dx: placement.dx, dy: placement.dy, where: "hidden" });
      continue;
    }
    result.set(placement.item.id, {
      dx: placement.dx,
      dy: placement.dy,
      // Candidate 0 is the only ATTACHED candidate (the only one on `GAP`); every other index sits
      // on `DISPLACED_GAP` and is therefore displaced. One rule, derived here once, so the renderer
      // never re-states it.
      where: placement.index === 0 ? "attached" : "displaced",
      slot: placement.index,
    });
  }

  return result;
}

/** Trailing `" (n/m)"` instance-suffix, as baked into `Combatant.name` at spawn
 *  (`packages/rules/src/encounter/spawn.ts`, e.g. `Cover (1/2)`). */
const SUFFIX_RE = / \(\d+\/\d+\)$/;

/** ~14 chars at 10px JetBrains Mono ≈ 88px, the target max label width. */
const DEFAULT_MAX_CHARS = 14;

/**
 * Render-side-only truncation — **never** call this on anything that gets written back to
 * `Combatant.name`. Parses a trailing `(n/m)` instance suffix off, end-ellipses the base so the
 * whole reassembled string fits `maxChars`, and re-appends the suffix untouched (it's load-bearing
 * for both the player — which section is this? — and `deployables.e2e.ts`'s panel locators, which
 * key off the FULL name and never see this function). No-op when `name` already fits.
 */
export function truncateLabel(name: string, maxChars: number = DEFAULT_MAX_CHARS): string {
  if (name.length <= maxChars) return name;

  const match = SUFFIX_RE.exec(name);
  const suffix = match ? match[0] : "";
  const base = suffix ? name.slice(0, name.length - suffix.length) : name;

  const available = Math.max(0, maxChars - suffix.length - 1);
  return `${base.slice(0, available)}…${suffix}`;
}
